import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { FaShareAlt, FaArrowLeft, FaHeart, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { getImageUrl } from '../utils/api';
import ProductQuestions from '../components/ProductQuestions';
import ProductReviews from '../components/ProductReviews';
import ProductImageGallery from '../components/ProductImageGallery';
import { useCart } from '../contexts/CartContext';
import { useFavorites } from '../contexts/FavoritesContext';

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding-top: 80px;
`;

const ProductContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const ProductHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const ProductTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  margin: 0;
  @media (max-width: 768px) {
    font-size: 1.4rem;
    text-align: center;
  }
`;

const ProductMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  color: #666;
  font-size: 1.1rem;
`;

const ProductId = styled.span`
  font-weight: 600;
`;

const ShareButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  
  &:hover {
    color: #ff6b35;
  }
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  padding: 40px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const BackToCatalogButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: none;
  border: 1px solid #ddd;
  border-radius: 8px;
  color: #666;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 20px;
  
  &:hover {
    border-color: #ff6b35;
    color: #ff6b35;
    background: #fff5f2;
  }
  
  svg {
    font-size: 0.9rem;
  }
`;

const ProductContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ProductImages = styled.div`
  position: relative;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ProductDescription = styled.p`
  font-size: 1.1rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

const PriceSection = styled.div`
  margin: 0;
`;

const Price = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
`;

const Weight = styled.div`
  color: #666;
  font-size: 1rem;
`;

const BuyButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
  
  &:hover {
    background: #45a049;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const CartControls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
`;

const FavoriteButton = styled.button`
  width: 100%;
  padding: 16px;
  background: ${props => props.$isFavorite ? '#ff6b35' : 'transparent'};
  color: ${props => props.$isFavorite ? 'white' : '#ff6b35'};
  border: 2px solid #ff6b35;
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  &:hover {
    background: #ff6b35;
    color: white;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const CartButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60px;
  
  &:hover {
    background: #45a049;
  }
  
  .top-text {
    font-size: 0.95rem;
    margin-bottom: 2px;
    line-height: 1.2;
  }
  
  .bottom-text {
    font-size: 0.85rem;
    opacity: 0.95;
    line-height: 1.2;
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
  height: 60px;
`;

const QuantityButton = styled.button`
  width: 50px;
  height: 60px;
  background: #f5f5f5;
  border: none;
  color: #333;
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #e0e0e0;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.div`
  min-width: 60px;
  padding: 0 16px;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  background: white;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TabsContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TabsHeader = styled.div`
  display: flex;
  border-bottom: 1px solid #eee;
`;

const TabButton = styled.button`
  flex: 1;
  padding: 20px;
  background: none;
  border: none;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.$active ? '#ff6b35' : '#666'};
  cursor: pointer;
  border-bottom: 3px solid ${props => props.$active ? '#ff6b35' : 'transparent'};
  transition: all 0.3s;
  
  &:hover {
    color: #ff6b35;
  }
`;

const TabContent = styled.div`
  padding: 30px;
`;

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const DescriptionTopRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$hasVideo ? '1fr 1fr' : '1fr'};
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  border-radius: 12px;
  background: #000;
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const DescriptionBottomSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DescriptionItem = styled.div`
  margin-bottom: 20px;
`;

const DescriptionTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const DescriptionText = styled.p`
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

const LabelsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const LabelChip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: #ff6b35;
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e55a2b;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(255, 107, 53, 0.3);
  }
`;

const GalleryOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GalleryPopupContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GalleryPopupImage = styled.img`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
`;

const GalleryPopupClose = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: #fff;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 8px;
  &:hover { opacity: 0.8; }
`;

const GalleryPopupArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255,255,255,0.2);
  border: none;
  color: #fff;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.4); }
  &.prev { left: 20px; }
  &.next { right: 20px; }
`;

const GalleryPopupCounter = styled.span`
  position: absolute;
  bottom: -36px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 0.95rem;
`;

const ProductDetail = () => {
  const { id: productId } = useParams();
  const navigate = useNavigate();
  const { addToCart, isInCart, getItemQuantity, updateQuantity, removeFromCart } = useCart();
  const { isFavorite, addToFavorites, removeFromFavorites } = useFavorites();
  const [activeTab, setActiveTab] = useState('description');
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [galleryPopupOpen, setGalleryPopupOpen] = useState(false);
  const [galleryPopupIndex, setGalleryPopupIndex] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/products/${productId}`);
        
        if (!response.ok) {
          throw new Error('Товар не найден');
        }
        
        const data = await response.json();
        console.log('Product API response:', data);
        
                  if (data.success) {
            const imageUrl = data.data.featured_image_url || getImageUrl(data.data.featured_image);
            
            // Обрабатываем массив изображений
            let images = [];
            if (data.data.images && Array.isArray(data.data.images)) {
              images = data.data.images.map(img => {
                if (typeof img === 'string') {
                  return img.startsWith('http') ? img : getImageUrl(img);
                }
                return img;
              });
            }
          
          // Преобразуем данные из PostgreSQL в формат компонента
          const productData = {
            id: data.data.id, // Используем реальный ID из базы
            name: data.data.name,
            brand: data.data.brand || data.data.brand_name || 'Без бренда',
            price: parseFloat(data.data.price),
            oldPrice: data.data.compare_price ? parseFloat(data.data.compare_price) : null,
            code: data.data.sku || '',
            weight: data.data.net_weight || (data.data.weight ? `${data.data.weight} г` : 'Не указано'),
            description: data.data.description || '',
            short_description: data.data.short_description || '',
            image: imageUrl,
            featured_image: imageUrl,
            images: images, // массив всех изображений
            rating: data.data.avg_rating ? Math.round(parseFloat(data.data.avg_rating)) : 0,
            reviewsCount: data.data.reviews_count || 0,
            inStock: (data.data.total_stock_quantity || 0) > 0,
            total_stock_quantity: parseInt(data.data.total_stock_quantity || 0),
            // Новые поля
            applicationArea: data.data.application_area || '',
            composition: data.data.composition ? data.data.composition.split(',').map(item => item.trim()) : [],
            nutrition: (() => {
              try {
                if (data.data.nutrition_facts) {
                  if (typeof data.data.nutrition_facts === 'string') {
                    return JSON.parse(data.data.nutrition_facts);
                  }
                  return data.data.nutrition_facts;
                }
              } catch (error) {
                console.error('Ошибка парсинга nutrition_facts:', error);
              }
              return {
                energy: 'Не указано',
                proteins: 'Не указано',
                fats: 'Не указано',
                carbs: 'Не указано'
              };
            })(),
            contraindications: data.data.contraindications ? 
              data.data.contraindications.split(',').map(item => item.trim()) : [],
            countryOfOrigin: data.data.country_of_origin || 'Не указано',
            shelfLife: data.data.shelf_life || 'Не указано',
            maxStorageTemp: data.data.max_storage_temp || 'Не указано',
            points: data.data.points || 0,
            labels: (() => {
              try {
                if (data.data.labels) {
                  if (typeof data.data.labels === 'string') {
                    return JSON.parse(data.data.labels);
                  } else if (Array.isArray(data.data.labels)) {
                    return data.data.labels;
                  }
                }
              } catch (error) {
                console.error('Ошибка парсинга labels:', error);
              }
              return [];
            })(),
            rutube_video_url: data.data.rutube_video_url || null,
            category_slug: data.data.category_slug || null
          };
          
          setProduct(productData);
        } else {
          throw new Error('Товар не найден');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  // Если страница открыта в новой вкладке (window.opener) — закрываем вкладку при «Назад»
  useEffect(() => {
    if (!window.opener) return;
    window.history.pushState({ closeTab: true }, '', window.location.href);
    const handlePopState = () => {
      window.close();
      setTimeout(() => { window.location.href = '/catalog'; }, 100);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleBuy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!product) return;
    
    // Проверяем наличие товара
    const stockQuantity = product.total_stock_quantity || 0;
    if (stockQuantity <= 0) {
      alert('Товар закончился, ожидайте');
      return;
    }
    
    setIsAddingToCart(true);
    try {
      addToCart(product);
      // Небольшая задержка для визуального отклика
      setTimeout(() => {
        setIsAddingToCart(false);
      }, 300);
    } catch (error) {
      console.error('Ошибка при добавлении в корзину:', error);
      setIsAddingToCart(false);
    }
  };

  const handleQuantityChange = (delta) => {
    if (!product) return;
    
    const currentQuantity = getItemQuantity(product.id);
    const newQuantity = currentQuantity + delta;
    
    if (newQuantity <= 0) {
      removeFromCart(product.id);
    } else {
      updateQuantity(product.id, newQuantity);
    }
  };

  const handleGoToCart = () => {
    navigate('/cart');
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = product?.name || 'Товар';
    const text = product?.description || 'Посмотрите этот товар';

    try {
      // Пробуем использовать Web Share API, если доступен
      if (navigator.share) {
        await navigator.share({
          title: title,
          text: text,
          url: url
        });
      } else {
        // Fallback: копируем ссылку в буфер обмена
        await navigator.clipboard.writeText(url);
        alert('Ссылка на товар скопирована в буфер обмена!');
      }
    } catch (error) {
      // Если пользователь отменил шаринг или произошла ошибка
      if (error.name !== 'AbortError') {
        // Пробуем fallback, если Web Share API не сработал
        try {
          await navigator.clipboard.writeText(url);
          alert('Ссылка на товар скопирована в буфер обмена!');
        } catch (clipboardError) {
          console.error('Ошибка при копировании ссылки:', clipboardError);
          alert('Не удалось скопировать ссылку. Попробуйте скопировать адрес вручную.');
        }
      }
    }
  };

  const isProductInCart = product ? isInCart(product.id) : false;
  const cartQuantity = product ? getItemQuantity(product.id) : 0;

  const galleryImages = useMemo(() => {
    if (!product) return [];
    const feat = product.image || product.featured_image || '/nofoto.png';
    const arr = feat ? [feat] : [];
    (product.images || []).forEach(img => {
      if (img !== feat && !arr.includes(img)) arr.push(img);
    });
    return arr;
  }, [product]);

  if (loading) {
    return (
      <Container>
        <ProductContainer>
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div>Загрузка товара...</div>
          </div>
        </ProductContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ProductContainer>
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <h2>Ошибка</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/catalog')}>Вернуться в каталог</button>
          </div>
        </ProductContainer>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container>
        <ProductContainer>
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <h2>Товар не найден</h2>
            <button onClick={() => navigate('/catalog')}>Вернуться в каталог</button>
          </div>
        </ProductContainer>
      </Container>
    );
  }

  return (
    <Container>
      <ProductContainer>
        <BackToCatalogButton onClick={() => {
          if (window.opener) {
            window.close();
            setTimeout(() => {
              navigate(product?.category_slug ? `/catalog/${product.category_slug}` : '/catalog');
            }, 100);
          } else {
            navigate(product?.category_slug ? `/catalog/${product.category_slug}` : '/catalog');
          }
        }}>
          <FaArrowLeft />
          {product?.category_slug ? 'Вернуться в категорию' : 'Вернуться в каталог'}
        </BackToCatalogButton>

        <ProductCard>
          <ProductHeader>
            <div>
              <ProductTitle>{product.name}</ProductTitle>
              
              {/* Метки товара */}
              {product.labels && Array.isArray(product.labels) && product.labels.length > 0 && (
                <LabelsContainer>
                  {product.labels.map((label, index) => (
                    <LabelChip
                      key={index}
                      onClick={() => {
                        navigate(`/catalog?label=${encodeURIComponent(label)}`);
                      }}
                    >
                      {label}
                    </LabelChip>
                  ))}
                </LabelsContainer>
              )}
              {product.brand && (
                <div style={{ marginTop: '10px', fontSize: '1rem', color: '#666', fontWeight: '600' }}>
                  {product.brand}
                </div>
              )}
            </div>
            <ProductMeta>
              <ProductId>#{product.id}</ProductId>
              <ShareButton onClick={handleShare}>
                <FaShareAlt />
              </ShareButton>
            </ProductMeta>
          </ProductHeader>

          <ProductContent>
          <ProductImages>
            <ProductImageGallery 
              images={product.images || []}
              featuredImage={product.image || product.featured_image || '/nofoto.png'}
              alt={product.name}
              onImageClick={(img, index) => {
                setGalleryPopupIndex(index);
                setGalleryPopupOpen(true);
              }}
            />
          </ProductImages>

          <ProductInfo>
            <ProductDescription>{product.short_description || ''}</ProductDescription>

            <PriceSection>
              {product.compare_price && product.compare_price > product.price && (
                <div style={{ fontSize: '1.5rem', color: '#999', textDecoration: 'line-through', marginBottom: '5px' }}>
                  {product.compare_price.toLocaleString()} ₽
                </div>
              )}
              <Price>{product.price.toLocaleString()} ₽</Price>
              <Weight>Вес: {product.weight}</Weight>
            </PriceSection>

            <ButtonGroup>
              {isProductInCart && cartQuantity > 0 ? (
                <CartControls>
                  <CartButton type="button" onClick={handleGoToCart}>
                    <span className="top-text">В корзине</span>
                    <span className="bottom-text">Перейти</span>
                  </CartButton>
                  <QuantitySelector>
                    <QuantityButton 
                      type="button"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={cartQuantity <= 1}
                    >
                      −
                    </QuantityButton>
                    <QuantityDisplay>{cartQuantity}</QuantityDisplay>
                    <QuantityButton 
                      type="button"
                      onClick={() => handleQuantityChange(1)}
                    >
                      +
                    </QuantityButton>
                  </QuantitySelector>
                </CartControls>
              ) : (
                <BuyButton 
                  type="button"
                  onClick={handleBuy}
                  disabled={!product || isAddingToCart || (product.total_stock_quantity || 0) <= 0}
                >
                  {isAddingToCart ? 'Добавляем...' : 
                   (product.total_stock_quantity || 0) <= 0 ? 'Товар закончился, ожидайте' : 
                   'Добавить в корзину'}
                </BuyButton>
              )}
              
              {product && (
                <FavoriteButton
                  type="button"
                  $isFavorite={isFavorite(product.id)}
                  onClick={async () => {
                    try {
                      if (isFavorite(product.id)) {
                        await removeFromFavorites(product.id);
                      } else {
                        await addToFavorites(product.id);
                      }
                    } catch (error) {
                      console.error('Error toggling favorite:', error);
                      if (error.message && error.message.includes('авторизация')) {
                        alert('Необходима авторизация для добавления в избранное');
                      } else {
                        alert('Ошибка при изменении избранного');
                      }
                    }
                  }}
                >
                  <FaHeart />
                  {isFavorite(product.id) ? 'В избранном' : 'Добавить в избранное'}
                </FavoriteButton>
              )}
            </ButtonGroup>
          </ProductInfo>
          </ProductContent>
        </ProductCard>

        <TabsContainer>
          <TabsHeader>
            <TabButton 
              $active={activeTab === 'description'} 
              onClick={() => setActiveTab('description')}
            >
              Описание
            </TabButton>
            <TabButton 
              $active={activeTab === 'reviews'} 
              onClick={() => setActiveTab('reviews')}
            >
              Отзывы
            </TabButton>
            <TabButton 
              $active={activeTab === 'questions'} 
              onClick={() => setActiveTab('questions')}
            >
              Вопросы
            </TabButton>
          </TabsHeader>

          <TabContent>
            {activeTab === 'description' && (
              <DescriptionSection>
                <DescriptionTopRow $hasVideo={!!product.rutube_video_url}>
                  {/* Левая колонка: Rutube видео */}
                  {product.rutube_video_url && (
                    <VideoContainer>
                      <iframe
                        src={(() => {
                          // Извлекаем ID видео из URL Rutube
                          const url = product.rutube_video_url;
                          let videoId = '';
                          
                          // Формат: https://rutube.ru/video/VIDEO_ID/
                          const match1 = url.match(/rutube\.ru\/video\/([^\/\?]+)/);
                          if (match1) {
                            videoId = match1[1];
                          } else {
                            // Формат: https://rutube.ru/play/embed/VIDEO_ID
                            const match2 = url.match(/rutube\.ru\/play\/embed\/([^\/\?]+)/);
                            if (match2) {
                              videoId = match2[1];
                            }
                          }
                          
                          return videoId ? `https://rutube.ru/play/embed/${videoId}` : '';
                        })()}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Rutube video player"
                      />
                    </VideoContainer>
                  )}
                  
                  {/* Правая колонка: Описание */}
                  {product.description && (
                    <DescriptionItem>
                      <DescriptionTitle>Описание</DescriptionTitle>
                      <DescriptionText>{product.description}</DescriptionText>
                    </DescriptionItem>
                  )}
                </DescriptionTopRow>
                
                {/* Нижняя секция: все остальные характеристики */}
                <DescriptionBottomSection>
                  <div>
                    <DescriptionItem>
                      <DescriptionTitle>Область применения</DescriptionTitle>
                      <DescriptionText>
                        {product.applicationArea || 'Рекомендуется: для бодрости и энергии в течении дня.'}
                      </DescriptionText>
                    </DescriptionItem>
                    
                    <DescriptionItem>
                      <DescriptionTitle>Состав</DescriptionTitle>
                      <DescriptionText>
                        {product.composition && product.composition.length > 0 
                          ? product.composition.join(', ')
                          : 'Не указано'}
                      </DescriptionText>
                    </DescriptionItem>
                    
                    {product.contraindications && product.contraindications.length > 0 && (
                      <DescriptionItem>
                        <DescriptionTitle>Противопоказания</DescriptionTitle>
                        <DescriptionText>
                          {product.contraindications.join(', ')}
                        </DescriptionText>
                      </DescriptionItem>
                    )}
                  </div>
                  
                  <div>
                    <DescriptionItem>
                      <DescriptionTitle>Масса нетто</DescriptionTitle>
                      <DescriptionText>{product.weight}</DescriptionText>
                    </DescriptionItem>
                    
                    <DescriptionItem>
                      <DescriptionTitle>Страна производства</DescriptionTitle>
                      <DescriptionText>{product.countryOfOrigin || 'Россия'}</DescriptionText>
                    </DescriptionItem>
                    
                    <DescriptionItem>
                      <DescriptionTitle>Срок годности</DescriptionTitle>
                      <DescriptionText>{product.shelfLife || '24 месяца'}</DescriptionText>
                    </DescriptionItem>
                    
                    <DescriptionItem>
                      <DescriptionTitle>Максимальная температура хранения</DescriptionTitle>
                      <DescriptionText>{product.maxStorageTemp || '+20C°'}</DescriptionText>
                    </DescriptionItem>
                  </div>
                </DescriptionBottomSection>
              </DescriptionSection>
            )}

            {activeTab === 'reviews' && (
              <ProductReviews productId={productId} />
            )}

            {activeTab === 'questions' && (
              <ProductQuestions productId={productId} />
            )}
          </TabContent>
        </TabsContainer>
      </ProductContainer>

      {galleryPopupOpen && galleryImages.length > 0 && (
        <GalleryOverlay onClick={() => setGalleryPopupOpen(false)}>
          <GalleryPopupContent onClick={e => e.stopPropagation()}>
            <GalleryPopupClose onClick={() => setGalleryPopupOpen(false)} aria-label="Закрыть">
              <FaTimes />
            </GalleryPopupClose>
            {galleryImages.length > 1 && (
              <>
                <GalleryPopupArrow className="prev" onClick={() => setGalleryPopupIndex(i => (i === 0 ? galleryImages.length - 1 : i - 1))}>
                  <FaChevronLeft />
                </GalleryPopupArrow>
                <GalleryPopupArrow className="next" onClick={() => setGalleryPopupIndex(i => (i === galleryImages.length - 1 ? 0 : i + 1))}>
                  <FaChevronRight />
                </GalleryPopupArrow>
              </>
            )}
            <GalleryPopupImage src={galleryImages[galleryPopupIndex]} alt={product?.name || 'Фото'} />
            <GalleryPopupCounter>{galleryPopupIndex + 1} / {galleryImages.length}</GalleryPopupCounter>
          </GalleryPopupContent>
        </GalleryOverlay>
      )}
    </Container>
  );
};

export default ProductDetail; 